/**
 * TaskPreviewContent — the `ahoy_task` entry in ENTITY_CONTENT_REGISTRY.
 *
 * A task is not a note (they were split deliberately — assigned rather than
 * authored, rides a stage board), so wherever an action card previews a task
 * — delete, update, hidden-entity, link/merge — it renders the canonical
 * TaskCard from the task family, never the note-shaped prose preview.
 *
 * Full mode hydrates the task from the CRM store (with associations — the
 * assignee and stage ride the association list) and renders TaskCard
 * read-only: no save handlers, so every row is a value, and the title is the
 * click-to-open affordance (the injected `onClick` from CrmClickableContent,
 * which opens the task slideout). While the store hydrates — or for a task
 * that does not exist yet — the card paints from the action's own payload
 * (`flatProps`) and fills in as data arrives.
 *
 * Compact mode matches the chip shape every other entity type uses in
 * Link/Merge LinkedPairs: name + Task type pill via CompactCardLayout.
 *
 * TaskCard carries its own card border, so ActionEntityPreview must NOT wrap
 * this content in a bordered ObjectCard (see its `selfBordered` gate);
 * variant tinting is applied by TaskCard itself via the `variant` prop
 * threaded through EntityContentComponentProps.
 */

import { useMemo } from 'react';

import { useAssociationTypes } from '@/lib/crm/hooks/useAssociationTypes';
import { usePropertySchema } from '@/lib/crm/hooks/usePropertySchema';
import { CompactCardLayout } from '@/lib/crm/object-cards/CompactCardLayout';
import type { Note } from '@/lib/crm/record/memo/notes.types';
import {
  extractMemoData,
  getMemoAssociationNames,
} from '@/lib/crm/record/memo/notes.utils';
import {
  TASK_ENTITY_TYPE,
  TASK_STAGE_ASSOCIATION_TYPE_NAME,
} from '@/lib/crm/record/task/task.constants';
import { TaskCard } from '@/lib/crm/record/task/TaskCard';
import { useCrmObject } from '@/lib/crm/store';

// Type-only import from the registry file (erased at compile time — no
// runtime cycle). Never import a VALUE from CreateActionCard.helpers here.
import type { EntityContentComponentProps } from './CreateActionCard.helpers';
import { formatValue } from './formatValue';

/** The task as TaskCard reads it, synthesized from the action's flatProps. */
function taskFromFlatProps(
  entityId: string | undefined,
  flatProps: Record<string, unknown>,
  fallbackTitle: string | undefined
): Note {
  return {
    entity_id: entityId ?? '',
    title: formatValue(flatProps['ahoy_title']) || fallbackTitle || '',
    content: formatValue(flatProps['ahoy_content']),
    created_at: '',
    updated_at: '',
    owner_entity_id: null,
    last_event_seq: 0,
    date: formatValue(flatProps['ahoy_date']) || undefined,
    datetime: formatValue(flatProps['ahoy_datetime']) || undefined,
    stage_entity_id: null,
    creator_entity_id: null,
    is_private: false,
  };
}

export function TaskPreviewContent({
  entityId,
  orgId,
  flatProps,
  enrichedData,
  onClick,
  compact,
  hideTypeBadge,
  variant,
}: EntityContentComponentProps) {
  const { owner: ownerAssociationName } =
    getMemoAssociationNames(TASK_ENTITY_TYPE);

  // The full TaskCard is the only branch that reads `schema` /
  // `assigneeAssociationType`; compact chips (Link/Merge) and the no-orgId
  // degrade render name + type pill from flatProps only. Bind both schema
  // hooks to their disabled sentinel (0 / undefined → the hooks' `enabled`
  // gate keeps them off the network) unless we're actually rendering that
  // full card, so a compact preview does no schema/association-type fetch.
  const rendersFullCard = !compact && Boolean(orgId);
  const { schema } = usePropertySchema(
    rendersFullCard ? (orgId ?? 0) : 0,
    TASK_ENTITY_TYPE
  );
  const { associationTypes } = useAssociationTypes(
    rendersFullCard ? orgId : undefined
  );
  const assigneeAssociationType = useMemo(
    () =>
      associationTypes.find(
        at => at.association_type_name === ownerAssociationName
      ),
    [associationTypes, ownerAssociationName]
  );

  // Compact chips don't render the card body — skip the store fetch. The
  // `enabled` gate keeps the ?? 0 orgId binding off the network.
  const { object: taskObject } = useCrmObject(
    orgId ?? 0,
    TASK_ENTITY_TYPE,
    compact ? undefined : entityId,
    { includeAssociations: true, enabled: Boolean(orgId) }
  );

  const task = useMemo(() => {
    if (taskObject) {
      return extractMemoData(
        taskObject,
        assigneeAssociationType?.association_type_name ?? null,
        TASK_STAGE_ASSOCIATION_TYPE_NAME
      );
    }
    return taskFromFlatProps(entityId, flatProps, enrichedData?.object_label);
  }, [
    taskObject,
    assigneeAssociationType?.association_type_name,
    entityId,
    flatProps,
    enrichedData?.object_label,
  ]);

  // Compact chips (Link/Merge) render name + type pill only. The full TaskCard
  // needs a real orgId for its store-backed rows (assignee badge, stage link,
  // due date), so when org context isn't available we degrade to the same chip
  // rather than dropping the preview entirely — the task's name (from the
  // payload) stays visible and click-to-open.
  // Narrows `orgId` to a number for the full-card render below; logically the
  // inverse of `rendersFullCard`.
  if (compact || !orgId) {
    const name =
      formatValue(flatProps['ahoy_title']) ||
      enrichedData?.object_label ||
      'Task';
    return (
      <CompactCardLayout
        name={name}
        entityTypeName={TASK_ENTITY_TYPE}
        onClick={onClick}
        hideTypeBadge={hideTypeBadge}
      />
    );
  }

  return (
    <TaskCard
      task={task}
      orgId={orgId}
      stageEntityId={task.stage_entity_id ?? null}
      ownerEntityId={task.owner_entity_id ?? null}
      dueDateSchema={schema['ahoy_date']}
      assigneeAssociationType={assigneeAssociationType}
      onTitleClick={onClick}
      variant={variant}
    />
  );
}
